import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Native platform detection hooks.
 *
 * Used to hide external payment flows (Stripe) inside native mobile apps,
 * which is required to comply with:
 *  - Apple App Store policies (iOS)
 *  - Google Play Billing policies (Android)
 *
 * Web remains unchanged.
 */

const detectPlatform = (): 'ios' | 'android' | 'web' => {
  try {
    if (!Capacitor.isNativePlatform()) return 'web';
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android' ? p : 'web';
  } catch {
    return 'web';
  }
};

export const useIsNativeIOS = () => {
  // Synchronous initial value: avoids a first render where web-only
  // (Stripe) UI could flash inside the native shell.
  const [isNativeIOS, setIsNativeIOS] = useState(() => detectPlatform() === 'ios');

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();
    setIsNativeIOS(isNative && platform === 'ios');
  }, []);

  return isNativeIOS;
};

export const useIsNativeAndroid = () => {
  const [isNativeAndroid, setIsNativeAndroid] = useState(() => detectPlatform() === 'android');

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();
    setIsNativeAndroid(isNative && platform === 'android');
  }, []);

  return isNativeAndroid;
};

/**
 * True when running inside the native iOS OR native Android Capacitor shell.
 * Use this to gate any UI that triggers external (Stripe) payment flows.
 */
export const useIsNativeMobile = () => {
  const [isNativeMobile, setIsNativeMobile] = useState(() => detectPlatform() !== 'web');

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();
    setIsNativeMobile(isNative && (platform === 'ios' || platform === 'android'));
  }, []);

  return isNativeMobile;
};

type Lang = 'es' | 'en';

interface NativePremiumMessageProps {
  language: string;
  /**
   * Optional platform override. When omitted, the component infers the
   * current Capacitor platform and renders the appropriate copy.
   */
  platform?: 'ios' | 'android';
}

/**
 * Component shown in place of Stripe purchase buttons inside native apps.
 * Copy adapts automatically to iOS vs Android compliance requirements.
 */
export const NativePremiumMessage = ({ language, platform }: NativePremiumMessageProps) => {
  const lang: Lang = language === 'en' ? 'en' : 'es';

  const detected: 'ios' | 'android' | 'web' =
    platform ??
    (Capacitor.isNativePlatform()
      ? (Capacitor.getPlatform() as 'ios' | 'android')
      : 'web');

  // Neutral copy for native apps: no mention of website, external stores,
  // pricing, or steering language ("upgrade", "subscribe", "purchase").
  // Required for Apple App Store Guideline 3.1.3(b) compliance.
  const messages: Record<'ios' | 'android' | 'web', Record<Lang, string>> = {
    ios: {
      es: 'Las funciones Premium requieren una suscripción activa.',
      en: 'Premium features require an active subscription.',
    },
    android: {
      es: 'Las funciones Premium requieren una suscripción activa.',
      en: 'Premium features require an active subscription.',
    },
    web: {
      es: 'Las funciones Premium requieren una suscripción activa.',
      en: 'Premium features require an active subscription.',
    },
  };

  return (
    <div className="bg-muted/50 border border-border rounded-lg p-6 text-center">
      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-6 h-6 text-primary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-sm text-muted-foreground">{messages[detected][lang]}</p>
    </div>
  );
};

/**
 * Backwards-compatible alias. Existing iOS call sites keep working unchanged;
 * the component now infers platform internally, so on Android it shows the
 * Google Play copy automatically.
 */
export const IOSPremiumMessage = NativePremiumMessage;
